<template>
  <slot
    :accordionBlockId="accordionBlockId"
    :handleKeydown="handleKeydown"
    :focusTargetExpandable="focusTargetExpandable"
    :getFocusPrevNextElement="getFocusPrevNextElement"
    :getFocusFirstLastElement="getFocusFirstLastElement">
  </slot>
</template>

<script>
import {defineComponent} from 'vue';
import AccordionItem from "./AccordionItem.vue";

export default defineComponent({
  name: 'AccordionBlock',
  components: [
    AccordionItem,
  ],
  props: {
    accordionBlockId: {
      type: String,
      default: null,
    }
  },
  setup() {
    /**
     * Event handler for keydown events.
     * Listens to up|down|home|end keys.
     *
     * @param Event event
     */
    const handleKeydown = (event) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          focusTargetExpandable(event, 'previous');

          break;
        case 'ArrowDown':
          event.preventDefault();
          focusTargetExpandable(event, 'next');

          break;
        case 'Home':
          event.preventDefault();
          focusTargetExpandable(event, 'first');

          break;
        case 'End':
          event.preventDefault();
          focusTargetExpandable(event, 'last');

          break;
        default:
          break;
      }
    };

    /**
     * Focuses the expandable element based on the given direction.
     *
     * @param Event event
     * @param string direction
     */
    const focusTargetExpandable = (event, direction) => {
      // Button selector class
      const buttonClass = '.accordion-block__button';

      // Get currently focused element
      const focusedEl = document.activeElement;

      // Go up 2 parent elements based on markup
      const listItemEl = focusedEl.parentElement.parentElement;

      // Gets the next element to focus based on the given direction
      let targetItemEl = null;
      switch (direction) {
        case 'previous':
          targetItemEl = getFocusPrevNextElement(listItemEl, 'previous');

          break;
        case 'next':
          targetItemEl = getFocusPrevNextElement(listItemEl, 'next');

          break;
        case 'first':
          targetItemEl = getFocusFirstLastElement(listItemEl, 'previous');

          break;
        case 'last':
          targetItemEl = getFocusFirstLastElement(listItemEl, 'next');

          break;
        default:
          break;
      }

      // Early return if the element still does not exist
      if (!targetItemEl) {
        return;
      }

      // Drill down to find focusable button element
      const buttonEl = targetItemEl.querySelector(buttonClass);

      // Focus element
      buttonEl.focus();
    };

    /**
     * Returns the previous or next element to
     * focus based on the given direction.
     *
     * @param Element targetEl
     * @param string direction
     *
     * @returns Element
     */
    const getFocusPrevNextElement = (targetEl, direction) => {
      let listItemEl = targetEl;

      // Get next target element
      let targetItemEl = listItemEl[`${direction}ElementSibling`];

      // CASE: If the sibling element is not available then we are
      // either at the first or last item of the list. We traverse
      // the list until we find the first or last item of the list
      // creating a loop-like effect with keyboard navigation.
      if (!targetItemEl) {
        const oppositeDirection = direction === 'previous' ? 'next' : 'previous';

        while (listItemEl[`${oppositeDirection}ElementSibling`]) {
          targetItemEl = listItemEl[`${oppositeDirection}ElementSibling`];
          listItemEl = targetItemEl;
        }
      }

      return targetItemEl;
    };

    /**
     * Returns the first or last element to
     * focus based on the given direction.
     *
     * @param Element targetEl
     * @param string direction
     *
     * @returns Element
     */
    const getFocusFirstLastElement = (targetEl, direction) => {
      let listItemEl = targetEl;

      // Traverse the elements until we find the target element
      let targetItemEl = listItemEl;
      while (listItemEl[`${direction}ElementSibling`]) {
        targetItemEl = listItemEl[`${direction}ElementSibling`];
        listItemEl = targetItemEl;
      }

      return targetItemEl;
    };

    return {
      handleKeydown,
      focusTargetExpandable,
      getFocusPrevNextElement,
      getFocusFirstLastElement,
    }
  },
});

</script>
